import type { ChipProps } from '@mui/material'
import type { TFunction } from 'i18next'
import type { EnumsResponse } from '@/api/enums'
import type { AuthEvent, AuthEventType } from '@/types/authEvents'

export const AUTH_EVENT_COLOR: Record<AuthEventType, ChipProps['color']> = {
  login_ok: 'success',
  login_failed: 'warning',
  refresh_ok: 'default',
  refresh_failed: 'error',
  logout: 'info',
  token_rejected: 'warning',
  tokens_invalidated: 'warning',
  client_forced_logout: 'error',
}

/**
 * Рядовое событие (продление) — контурный бейдж: заливка default в теме
 * совпадает с фоном таблицы, и бейдж читался бы как простой текст.
 */
export function authEventChipVariant(type: AuthEventType): ChipProps['variant'] {
  return AUTH_EVENT_COLOR[type] === 'default' ? 'outlined' : 'filled'
}

/** Коды отказа POST /auth/refresh (AuthRefreshError), которые SPA кладёт в причину `refresh_401:<код>` */
const REFRESH_CODES = [
  'token_blacklisted',
  'refresh_expired',
  'token_invalid',
  'token_missing',
  'token_invalidated',
  'no_cabinet',
] as const

/** Причины принудительного выхода, которые выставляет сама SPA (api/refreshToken.ts) */
const CLIENT_REASONS = ['no_token', 'impersonation_401'] as const

const PRIOR_STATES = ['manual', 'no_storage', 'storage_unauth', 'storage_auth'] as const

const REFRESH_401_PREFIX = 'refresh_401:'
const FORCED_PREFIX = 'forced:'

function includes<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value)
}

/** Подпись значения справочника GET /enums; нет справочника — само значение */
export function enumLabel(enums: EnumsResponse | null, enumName: string, value: string): string {
  return enums?.[enumName]?.find((option) => option.value === value)?.label ?? value
}

export function eventTypeLabel(enums: EnumsResponse | null, type: AuthEventType): string {
  return enumLabel(enums, 'AuthEventType', type)
}

/** Причина принудительного выхода от клиента — человеческим языком */
export function describeClientReason(reason: string, t: TFunction): string {
  if (reason.startsWith(REFRESH_401_PREFIX)) {
    const code = reason.slice(REFRESH_401_PREFIX.length)
    let codeLabel: string
    if (includes(REFRESH_CODES, code)) {
      codeLabel = t(`admin.users.authEvents.refreshCodes.${code}`)
    } else if (code === 'unknown') {
      // Сервер ответил 401 без кода (старый API или прокси)
      codeLabel = t('admin.users.authEvents.refreshCodes.noCode')
    } else {
      codeLabel = t('admin.users.authEvents.refreshCodes.other', { code })
    }

    return t('admin.users.authEvents.clientReasons.refresh401', { reason: codeLabel })
  }
  if (includes(CLIENT_REASONS, reason)) {
    return t(`admin.users.authEvents.clientReasons.${reason}`)
  }

  return reason
}

/** Что клиент знал о сессии перед входом (prior_state) */
export function describePriorState(priorState: string, t: TFunction): string {
  if (priorState.startsWith(FORCED_PREFIX)) {
    return t('admin.users.authEvents.priorStates.forced', {
      reason: describeClientReason(priorState.slice(FORCED_PREFIX.length), t),
    })
  }
  if (includes(PRIOR_STATES, priorState)) {
    return t(`admin.users.authEvents.priorStates.${priorState}`)
  }

  return priorState
}

/** Причина события: серверная — из справочника, клиентская — из словаря SPA */
export function describeReason(event: AuthEvent, enums: EnumsResponse | null, t: TFunction): string | null {
  if (!event.reason) {
    return null
  }
  if (event.event === 'client_forced_logout') {
    return describeClientReason(event.reason, t)
  }

  return enumLabel(enums, 'AuthEventReason', event.reason)
}

const BROWSERS: ReadonlyArray<[RegExp, string]> = [
  [/YaBrowser\/(\d+)/, 'Yandex'],
  [/Edg(?:A|iOS)?\/(\d+)/, 'Edge'],
  [/OPR\/(\d+)/, 'Opera'],
  [/Firefox\/(\d+)/, 'Firefox'],
  [/FxiOS\/(\d+)/, 'Firefox'],
  [/CriOS\/(\d+)/, 'Chrome'],
  [/Chrome\/(\d+)/, 'Chrome'],
  [/Version\/(\d+)[\d.]* (?:Mobile\/\S+ )?Safari\//, 'Safari'],
  [/Dart\/(\d+)/, 'Dart'],
]

const SYSTEMS: ReadonlyArray<[RegExp, string]> = [
  [/Windows/, 'Windows'],
  [/Android/, 'Android'],
  [/iPhone|iPad|iPod/, 'iOS'],
  [/Mac OS X|Macintosh/, 'macOS'],
  [/CrOS/, 'ChromeOS'],
  [/Linux/, 'Linux'],
]

/** Короткое имя устройства из user agent: «Chrome 128 · Windows»; не распознали — null */
export function shortUserAgent(userAgent: string | null): string | null {
  if (!userAgent) {
    return null
  }

  let browser: string | null = null
  for (const [pattern, name] of BROWSERS) {
    const match = pattern.exec(userAgent)
    if (match) {
      browser = `${name} ${match[1]}`
      break
    }
  }
  const system = SYSTEMS.find(([pattern]) => pattern.test(userAgent))?.[1] ?? null

  const parts = [browser, system].filter((part): part is string => part !== null)

  return parts.length > 0 ? parts.join(' · ') : null
}

/** Короткий идентификатор вкладки для таблицы (полный — в подробностях) */
export function shortTabId(tabId: string | null): string | null {
  return tabId ? tabId.slice(0, 8) : null
}
